import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../../public/logo.PNG";
import "./Header.css";

const NAV_CATEGORIES = [
  "Politics", "Entertainment", "Sports",
  "Business", "Technology", "Security", "Education", "Health", "Viral",
];

const SearchIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const AdminIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

export default function Header() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    navigate(`/?search=${encodeURIComponent(searchTerm.trim())}`);
    setSearchOpen(false);
    setSearchTerm("");
  };

  const isActive    = (path) => location.pathname === path;
  const isCatActive = (cat)  => location.pathname === `/category/${cat.toLowerCase()}`;

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  /* Admin button: if logged in → /admin, else → /login */
  const adminHref = user ? "/admin" : "/login";

  return (
    <header className={`header${scrolled ? " header--scrolled" : ""}`}>

      <div className="header-brand">
        <div className="container header-brand-inner">

          <div className="brand-left">
            <span className="hdr-date">
              <CalendarIcon />
              <span className="hdr-date-text">{today}</span>
            </span>
            <Link to={adminHref} className="btn-admin">
              <AdminIcon />
              <span>{user ? "Dashboard" : "Admin"}</span>
            </Link>
          </div>

          <Link to="/" className="logo">
            <img src={logo} alt="Logo" />
          </Link>

          <div className="brand-right">
            <button
              className={`search-toggle${searchOpen ? " active" : ""}`}
              onClick={() => setSearchOpen(o => !o)}
              aria-label="Toggle search"
            >
              <SearchIcon />
            </button>
          </div>
        </div>

        <div className={`search-drawer${searchOpen ? " search-drawer--open" : ""}`}>
          <div className="container">
            <form className="search-form" onSubmit={handleSearch}>
              <span className="search-icon-wrap"><SearchIcon /></span>
              <input
                type="search"
                placeholder="Search articles, topics, categories…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="search-input"
                autoFocus={searchOpen}
              />
              <button type="submit" className="search-submit">Search</button>
            </form>
          </div>
        </div>
      </div>

      <nav className="header-nav">
        <div className="container nav-inner">
          <ul className={`nav-list${menuOpen ? " nav-open" : ""}`}>
            <li>
              <Link to="/" className={isActive("/") ? "nav-active" : ""}>Home</Link>
            </li>
            {NAV_CATEGORIES.map(cat => (
              <li key={cat}>
                <Link
                  to={`/category/${cat.toLowerCase()}`}
                  className={isCatActive(cat) ? "nav-active" : ""}
                >
                  {cat}
                </Link>
              </li>
            ))}
          </ul>
          <button className="hamburger" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </nav>
    </header>
  );
}
